import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, MessageSquare, MoreVertical, Edit, Copy, Trash2, Lock, Unlock } from 'lucide-react'
import { Avatar } from '../components/ui/Avatar'
import { Input } from '../components/ui/Input'
import { Skeleton } from '../components/ui/Skeleton'
import { ChatPanelMaster, type ChatPanelRef } from './ChatPage/ChatPanelMaster'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { TopNavigation } from '../components/TopNavigation'
import { SettingsModal } from '../components/SettingsModal'
import { useUser } from '../contexts/UserContext'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { supabase } from '../lib/supabase'
import { CreateAgentModal } from '../components/CreateAgentModal'
import { DeleteAgentModal } from '../components/DeleteAgentModal'
import { cleanupAgentData } from '../services/agentCleanupService'
import { useLLM } from '../hooks/useLLM'

interface Agent {
    id: string
    name: string
    handle: string
    avatar?: string
    function: string
    status: 'active' | 'draft'
    version: string
    quality: string
    model: string
    updatedAt: string
    is_locked?: boolean
}

// Mock agents removed. Now using Supabase.

function AgentCard({
    agent,
    onDuplicate,
    onDelete,
    onToggleLock
}: {
    agent: Agent
    onDuplicate: (agent: Agent) => void
    onDelete: (agent: Agent) => void
    onToggleLock: (agent: Agent) => void
}) {
    const navigate = useNavigate()
    const [showMenu, setShowMenu] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleCardClick = () => {
        navigate(`/agentes/${agent.handle}`)
    }

    const handleProfileClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        console.log('Profile Summary clicked, navigating to:', `/agentes/${agent.handle}`)
        navigate(`/agentes/${agent.handle}`)
    }

    const handleConfigClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        navigate(`/agentes/${agent.handle}/configuracao`)
    }

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowMenu(!showMenu)
    }

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowMenu(false)
        navigate(`/agentes/${agent.handle}/configuracao`)
    }

    const handleDuplicate = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowMenu(false)
        onDuplicate(agent)
    }

    const handleToggleLock = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowMenu(false)
        onToggleLock(agent)
    }

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowMenu(false)
        onDelete(agent)
    }

    return (
        <div
            onClick={handleCardClick}
            className="rounded-2xl p-[1px] bg-gray-200 dark:bg-gradient-to-br dark:from-blue-400/40 dark:via-purple-500/40 dark:to-pink-400/30 border-0 hover:dark:from-blue-400/50 hover:dark:via-purple-500/50 hover:dark:to-pink-400/40 cursor-pointer transition-all"
        >
            <div className="bg-white dark:bg-black rounded-2xl p-4 h-full">
                {/* Header: Avatar + Menu Button */}
                <div className="flex items-start justify-between mb-3">
                    <Avatar size="lg" className="w-14 h-14" alt={agent.name} src={agent.avatar} />
                    {/* Options Menu */}
                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={handleMenuClick}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                            title="Opções"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>
                        {/* Dropdown Menu */}
                        {showMenu && (
                            <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                                <button
                                    onClick={handleEdit}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <Edit className="w-4 h-4" />
                                    Editar
                                </button>
                                <button
                                    onClick={handleDuplicate}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <Copy className="w-4 h-4" />
                                    Duplicar
                                </button>
                                <button
                                    onClick={handleToggleLock}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                >
                                    {agent.is_locked ? (
                                        <>
                                            <Unlock className="w-4 h-4" />
                                            Desbloquear
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="w-4 h-4" />
                                            Bloquear
                                        </>
                                    )}
                                </button>
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                                <button
                                    onClick={handleDelete}
                                    disabled={agent.is_locked}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${agent.is_locked
                                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                        : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                        }`}
                                    title={agent.is_locked ? 'Agente bloqueado - desbloqueie para deletar' : ''}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Deletar
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Nome + Badge Verificado + Lock Icon */}
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5 leading-tight flex items-center gap-1">
                    <span>{agent.name}</span>
                    {agent.status === 'active' && (
                        <svg className="w-4 h-4 text-[#1d9bf0] flex-shrink-0" viewBox="0 0 22 22" aria-label="Verified account" role="img">
                            <g>
                                <path fill="currentColor" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"></path>
                            </g>
                        </svg>
                    )}
                    {agent.is_locked && (
                        <span title="Agente bloqueado">
                            <Lock className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 flex-shrink-0" />
                        </span>
                    )}
                </h3>

                {/* Handle */}
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-2">{agent.handle}</p>

                {/* Descrição */}
                <p className="text-gray-700 dark:text-gray-300 text-xs mb-3 leading-relaxed line-clamp-2">
                    {agent.function}
                </p>

                {/* Estatísticas: Conversas e Score de Qualidade */}
                <div className="flex items-center gap-3 mb-4 text-xs">
                    <div>
                        <span className="text-gray-900 dark:text-white font-semibold">v{agent.version}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">Version</span>
                    </div>
                    <div>
                        <span className="text-gray-900 dark:text-white font-semibold">{agent.quality}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">Quality</span>
                    </div>
                </div>

                {/* Botões de ação */}
                <div className="flex gap-2">
                    <button
                        onClick={handleProfileClick}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        className="flex-1 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white rounded-full font-semibold text-xs hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors flex items-center justify-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Perfil
                    </button>
                    <button
                        onClick={handleConfigClick}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold text-xs transition-colors flex items-center justify-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Config
                    </button>
                </div>
            </div>
        </div >
    )
}

function AgentCardSkeleton() {
    return (
        <div className="rounded-2xl p-[1px] bg-gray-200 dark:bg-gray-800 border-0">
            <div className="bg-white dark:bg-black rounded-2xl p-4 h-full">
                {/* Header: Avatar + Button */}
                <div className="flex items-start justify-between mb-3">
                    <Skeleton className="w-14 h-14 rounded-full" />
                    <Skeleton className="w-8 h-8 rounded-lg" />
                </div>

                {/* Nome */}
                <Skeleton className="h-5 w-3/4 mb-2" />

                {/* Handle */}
                <Skeleton className="h-3 w-1/2 mb-3" />

                {/* Descrição */}
                <div className="space-y-1 mb-4">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                </div>

                {/* Stats */}
                <div className="flex gap-3 mb-4">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-16" />
                </div>

                {/* Button */}
                <Skeleton className="h-8 w-full rounded-full" />
            </div>
        </div>
    )
}

export default function AgentsListPage() {
    const navigate = useNavigate()
    const [isChatOpen, setIsChatOpen] = useState(true)
    const [isLoading, setIsLoading] = useState(true)
    const [chatPanelWidth, setChatPanelWidth] = useState(624)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [manualFullscreenOverride, setManualFullscreenOverride] = useState(false)
    const { isSettingsOpen, closeSettings, user, activeSettingsTab, setActiveSettingsTab } = useUser()
    const [agents, setAgents] = useState<Agent[]>([])
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const chatPanelRef = useRef<ChatPanelRef>(null)


    const isMobile = useMediaQuery('(max-width: 768px)')
    const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)')

    // Auto-enable fullscreen on mobile/tablet (using ref to prevent cascading renders)
    const hasInitializedFullscreen = useRef(false)

    useEffect(() => {
        if ((isMobile || isTablet) && isChatOpen && !manualFullscreenOverride && !isFullscreen && !hasInitializedFullscreen.current) {
            hasInitializedFullscreen.current = true
            queueMicrotask(() => setIsFullscreen(true))
        }
    }, [isMobile, isTablet, isChatOpen, manualFullscreenOverride, isFullscreen])

    // Reset initialization ref and fullscreen state when chat is closed
    useEffect(() => {
        if (!isChatOpen) {
            hasInitializedFullscreen.current = false
            // Wrap in microtask to avoid "setState synchronously within an effect" warning
            queueMicrotask(() => setManualFullscreenOverride(false))
            if (isFullscreen) {
                queueMicrotask(() => setIsFullscreen(false))
            }
        }
    }, [isChatOpen, isFullscreen])


    // Auto-Fullscreen Logic: Automatically switches to fullscreen if content area is too narrow
    useEffect(() => {
        const checkLayout = () => {
            if (isMobile) return; // Mobile is handled by media queries/isMobile flag

            const windowWidth = window.innerWidth;
            // If chat is open, calculate remaining space. If closed, irrelevant.
            if (isChatOpen) {
                const contentWidth = windowWidth - chatPanelWidth;
                const MIN_CONTENT_WIDTH = 350; // Minimum width for the agent list to be usable

                // Only auto-trigger fullscreen if user hasn't manually overridden it
                if (contentWidth < MIN_CONTENT_WIDTH && !isFullscreen && !manualFullscreenOverride) {
                    setIsFullscreen(true);
                } else if (contentWidth >= MIN_CONTENT_WIDTH && isFullscreen && !manualFullscreenOverride) {
                    // Optional: Auto-restore to side-by-side if space permits
                    // This creates a fluid experience where resizing the window back up restores the view
                    setIsFullscreen(false);
                }
            }
        };

        const handleResize = () => {
            // Reset manual override on window resize - user is actively changing window size
            setManualFullscreenOverride(false);
            checkLayout();
        };

        window.addEventListener('resize', handleResize);
        // Check immediately in case we load in a small window
        checkLayout();

        return () => window.removeEventListener('resize', handleResize);
    }, [chatPanelWidth, isChatOpen, isFullscreen, isMobile, manualFullscreenOverride]);

    // Settings state
    const [settings, setSettings] = useState({
        voiceEnabled: false,
        voiceRate: 1.0,
        voicePitch: 1.0,
        selectedVoice: '',
        quickRepliesEnabled: true,
        autoSendFeedback: false,
        dragDropEnabled: true,
        threadingEnabled: false,
        searchEnabled: true,
    })

    const { config: llmConfig, updateConfig: setLlmConfig } = useLLM()

    useEffect(() => {
        const fetchAgents = async () => {
            if (!user) {
                setIsLoading(false)
                return
            }

            setIsLoading(true)

            const { data, error } = await supabase
                .from('agents')
                .select('*')
                .eq('user_id', user.id)
                .order('updated_at', { ascending: false })

            if (error) {
                console.error('Error fetching agents:', error)
            } else if (data) {
                const formattedAgents: Agent[] = data.map(a => ({
                    id: a.id,
                    name: a.name,
                    handle: a.handle || '',
                    avatar: a.avatar_url || undefined,
                    function: a.function_description || '',
                    status: (a.status as 'active' | 'draft') || 'draft',
                    version: a.version || '1.0.0',
                    quality: a.quality_score ? `${a.quality_score}/10` : 'N/A',
                    model: a.model || 'N/A',
                    updatedAt: new Date(a.updated_at || Date.now()).toLocaleDateString()
                }))
                setAgents(formattedAgents)
            }
            setIsLoading(false)
        }

        fetchAgents()
    }, [user])



    // Handler for duplicating an agent
    const handleDuplicateAgent = async (agent: Agent) => {
        if (!user) return

        try {
            // Fetch original agent data from database
            const { data: originalAgent, error: fetchError } = await supabase
                .from('agents')
                .select('*')
                .eq('id', agent.id)
                .single()

            if (fetchError) throw fetchError

            // Generate new handle by appending copy number
            let copyNumber = 1
            let newHandle = `${originalAgent.handle}-copy${copyNumber}`

            // Check if handle exists and increment
            while (agents.some(a => a.handle === newHandle)) {
                copyNumber++
                newHandle = `${originalAgent.handle}-copy${copyNumber}`
            }

            // Create duplicated agent
            const { data: duplicatedAgent, error: insertError } = await supabase
                .from('agents')
                .insert({
                    user_id: user.id,
                    name: `${originalAgent.name} (Cópia ${copyNumber})`,
                    handle: newHandle,
                    function_description: originalAgent.function_description,
                    status: 'active', // All agents are active by default (verified badge)
                    version: '1.0.0', // Reset version
                    model: originalAgent.model,
                    quality_score: originalAgent.quality_score,
                    avatar_url: originalAgent.avatar_url
                })
                .select()
                .single()

            if (insertError) throw insertError

            // Add to UI
            const formattedAgent: Agent = {
                id: duplicatedAgent.id,
                name: duplicatedAgent.name,
                handle: duplicatedAgent.handle || '',
                avatar: duplicatedAgent.avatar_url || undefined,
                function: duplicatedAgent.function_description || '',
                status: (duplicatedAgent.status as 'active' | 'draft') || 'draft',
                version: duplicatedAgent.version || '1.0.0',
                quality: duplicatedAgent.quality_score ? `${duplicatedAgent.quality_score}/10` : 'N/A',
                model: duplicatedAgent.model || 'N/A',
                updatedAt: new Date(duplicatedAgent.updated_at || Date.now()).toLocaleDateString()
            }
            setAgents(prev => [formattedAgent, ...prev])

            // TODO: Copy agent files from original to duplicated agent
            console.log('Agent duplicated successfully:', duplicatedAgent.id)
        } catch (error) {
            console.error('Error duplicating agent:', error)
            alert('Erro ao duplicar agente')
        }
    }

    // Handler for toggling agent lock status
    const handleToggleLockAgent = async (agent: Agent) => {
        try {
            const newLockedState = !agent.is_locked

            // Update in database
            const { error } = await supabase
                .from('agents')
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .update({ is_locked: newLockedState } as any)
                .eq('id', agent.id)

            if (error) throw error

            // Update UI
            setAgents(prev => prev.map(a =>
                a.id === agent.id
                    ? { ...a, is_locked: newLockedState }
                    : a
            ))

            // Show feedback
            const message = newLockedState
                ? `🔒 Agente "${agent.name}" bloqueado com sucesso!\nEste agente não poderá ser deletado acidentalmente.`
                : `🔓 Agente "${agent.name}" desbloqueado.\nAgora pode ser deletado.`

            alert(message)
        } catch (error) {
            console.error('Error toggling agent lock:', error)
            alert('Erro ao alterar bloqueio do agente')
        }
    }

    // Handler for opening delete confirmation
    const handleDeleteAgent = (agent: Agent) => {
        // Check if agent is locked
        if (agent.is_locked) {
            alert('⚠️ Este agente está bloqueado!\n\nDesbloqueie o agente antes de deletá-lo.')
            return
        }

        setAgentToDelete(agent)
        setIsDeleteModalOpen(true)
    }

    // Handler for confirming delete
    const handleConfirmDelete = async () => {
        if (!agentToDelete) return

        setIsDeleting(true)
        try {
            console.log(`[Delete] Starting deletion process for agent: ${agentToDelete.name} (${agentToDelete.id})`)

            // Step 1: Clean up files and related records
            console.log('[Delete] Step 1: Cleaning up agent data...')
            const cleanupResult = await cleanupAgentData(agentToDelete.id)

            if (!cleanupResult.success) {
                console.warn('[Delete] Cleanup had errors:', cleanupResult.errors)
                // Continue with agent deletion even if cleanup had issues
                // User will be notified of partial cleanup
            } else {
                console.log('[Delete] Cleanup successful:', {
                    filesDeleted: cleanupResult.storageDeleted,
                    tablesDeleted: cleanupResult.tablesDeleted
                })
            }

            // Step 2: Delete agent from database
            console.log('[Delete] Step 2: Deleting agent from database...')
            const { error } = await supabase
                .from('agents')
                .delete()
                .eq('id', agentToDelete.id)

            if (error) throw error

            // Step 3: Remove from UI
            console.log('[Delete] Step 3: Updating UI...')
            setAgents(prev => prev.filter(a => a.id !== agentToDelete.id))

            // Step 4: Close modal
            setIsDeleteModalOpen(false)
            setAgentToDelete(null)

            // Step 5: Show success message with cleanup details
            const successMessage = cleanupResult.storageDeleted > 0 || cleanupResult.tablesDeleted.length > 0
                ? `Agente deletado com sucesso!\n\n📁 Arquivos removidos: ${cleanupResult.storageDeleted}\n🗄️ Tabelas limpas: ${cleanupResult.tablesDeleted.join(', ') || 'nenhuma'}`
                : 'Agente deletado com sucesso!'

            console.log('[Delete] Agent deleted successfully:', agentToDelete.id)
            alert(successMessage)

            // Log any cleanup errors (non-blocking)
            if (cleanupResult.errors.length > 0) {
                console.warn('[Delete] Cleanup warnings:', cleanupResult.errors)
            }
        } catch (error) {
            console.error('[Delete] Error deleting agent:', error)
            alert('Erro ao deletar agente. Por favor, tente novamente.')
        } finally {
            setIsDeleting(false)
        }
    }

    // Handler for agent created - navigate to config page
    const handleAgentCreated = (agent: { id: string; handle: string }) => {
        console.log('[Create] Agent created successfully:', agent)
        navigate(`/agentes/${agent.handle}/configuracao`)
    }

    // Handler for manual fullscreen changes (from ChatPanel buttons)
    const handleFullscreenChange = (newFullscreenState: boolean) => {
        setManualFullscreenOverride(true);
        setIsFullscreen(newFullscreenState);
    };

    // Handler for chat actions from MobileMenu
    const handleChatAction = (action: 'new' | 'history' | 'settings') => {
        // Ensure chat is visible
        if (!isChatOpen) setIsChatOpen(true);

        // Execute action on ChatPanel via ref
        if (action === 'new') chatPanelRef.current?.startNewConversation();
        if (action === 'history') chatPanelRef.current?.setShowHistory(true);
        if (action === 'settings') chatPanelRef.current?.setShowSettings(true);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black flex overflow-hidden">
            {/* Chat Panel */}
            <ErrorBoundary>
                <ChatPanelMaster
                    ref={chatPanelRef}
                    isOpen={isChatOpen}
                    // agentId is managed by internal state if not passed, or we can leave it undefined for "New Chat" state initially
                    onClose={() => setIsChatOpen(false)}
                    onWidthChange={setChatPanelWidth}
                    onFullscreenChange={handleFullscreenChange}
                    isFullscreen={isFullscreen}
                />
            </ErrorBoundary>

            {/* Settings Modal */}
            <SettingsModal
                isOpen={isSettingsOpen}
                onClose={closeSettings}
                settings={settings}
                onSettingsChange={setSettings}
                llmConfig={llmConfig}
                onLLMConfigChange={setLlmConfig}
                activeTab={activeSettingsTab}
                onTabChange={setActiveSettingsTab}
            />

            {/* Chat Toggle Button (Fixed) - Only shows when chat is closed */}
            {
                !isChatOpen && (
                    <button
                        onClick={() => setIsChatOpen(true)}
                        className="fixed left-4 bottom-4 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors z-40"
                        title="Abrir Chat"
                    >
                        <MessageSquare className="w-6 h-6" />
                    </button>
                )
            }
            {/* Top Navigation - Global Fixed, never moves */}
            <div className="fixed top-0 left-0 right-0 z-[60]">
                <TopNavigation
                    onChatAction={handleChatAction}
                    onCreateAgent={() => setIsCreateModalOpen(true)}
                />
            </div>

            {/* Main Content - Hidden when fullscreen */}
            {
                !isFullscreen && (
                    <div
                        className="flex flex-col flex-1 transition-all duration-300 @container pt-12 h-screen"
                        style={{
                            paddingLeft: isChatOpen && !isMobile ? `${chatPanelWidth}px` : '0px'
                        }}
                    >
                        {/* Dashboard Content */}
                        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-black">
                            <div className="w-full mx-auto px-6 py-6">
                                {/* Header: Title + Search + Button */}
                                <div className="mb-6">
                                    {/* Desktop Layout: Title on left, Search + Button on right */}
                                    <div className="hidden @xl:flex items-center justify-between gap-4 mb-4">
                                        <div>
                                            <h1 className="text-lg font-semibold text-gray-700 dark:text-gray-300 tracking-tight">Meta-Agents</h1>
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                                Gerencie todos os seus agentes de IA personalizados
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div className="w-80 relative group">
                                                {/* Gradient border wrapper */}
                                                <div className="rounded-full p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:dark:bg-gradient-to-br group-focus-within:dark:from-blue-400/40 group-focus-within:dark:via-purple-500/40 group-focus-within:dark:to-pink-400/30 transition-all duration-200">
                                                    <div className="relative bg-white dark:bg-gray-950 rounded-full">
                                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10" />
                                                        <Input
                                                            placeholder="Buscar agentes..."
                                                            className="pl-10 h-7 bg-transparent border-0 rounded-full dark:text-white dark:placeholder-gray-500 focus-visible:ring-0 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => setIsCreateModalOpen(true)}
                                                className="inline-flex items-center justify-center h-7 px-4 text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-full transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                                            >
                                                <Plus className="w-3.5 h-3.5 mr-2" />
                                                Criar Agente
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mobile Layout: Stacked */}
                                    <div className="@xl:hidden">
                                        <div className="mb-4">
                                            <h1 className="text-lg font-semibold text-gray-700 dark:text-gray-300 tracking-tight">Meta-Agents</h1>
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                                Gerencie todos os seus agentes de IA personalizados
                                            </p>
                                        </div>

                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 relative group">
                                                    {/* Gradient border wrapper */}
                                                    <div className="rounded-full p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:dark:bg-gradient-to-br group-focus-within:dark:from-blue-400/40 group-focus-within:dark:via-purple-500/40 group-focus-within:dark:to-pink-400/30 transition-all duration-200">
                                                        <div className="relative bg-white dark:bg-gray-950 rounded-full">
                                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10" />
                                                            <Input
                                                                placeholder="Buscar agentes..."
                                                                className="pl-10 h-7 bg-transparent border-0 rounded-full dark:text-white dark:placeholder-gray-500 focus-visible:ring-0 text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    console.log('Criar Agente button clicked')
                                                    setIsCreateModalOpen(true)
                                                }}
                                                className="w-full inline-flex items-center justify-center h-9 px-4 text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-full transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Criar Agente
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Agent Cards Grid */}
                                <div
                                    className="grid gap-4"
                                    style={{
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 320px))',
                                        willChange: 'auto',
                                        contain: 'layout'
                                    }}
                                >
                                    {isLoading ? (
                                        // Renderizar 8 skeletons enquanto carrega
                                        Array.from({ length: 8 }).map((_, i) => (
                                            <AgentCardSkeleton key={i} />
                                        ))
                                    ) : (
                                        agents.map((agent) => (
                                            <AgentCard
                                                key={agent.id}
                                                agent={agent}
                                                onDuplicate={handleDuplicateAgent}
                                                onDelete={handleDeleteAgent}
                                                onToggleLock={handleToggleLockAgent}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Create Agent Modal */}
            <CreateAgentModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onAgentCreated={handleAgentCreated}
            />

            {/* Delete Agent Modal */}
            <DeleteAgentModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false)
                    setAgentToDelete(null)
                }}
                onConfirm={handleConfirmDelete}
                agentName={agentToDelete?.name || ''}
                isDeleting={isDeleting}
            />
        </div >
    )
}
